import { useId, useState } from 'react';

import { Card } from '@/components/Card/Card';

import { supportErrorMessage } from './supportConsoleFormat';
import styles from './SupportConsolePage.module.css';
import { SupportSearchForm } from './SupportSearchForm';
import { SupportSearchResults } from './SupportSearchResults';
import { supportDisplayName, type SupportUserSummary } from './supportConsoleTypes';
import { SupportUserPanel } from './SupportUserPanel';
import { useSupportUserSearch } from './useSupportConsole';

function searchOutcome(users: SupportUserSummary[]): string {
  if (users.length === 0) {
    return 'No user has that ID or email.';
  }
  if (users.length === 1) {
    return `1 user found: ${supportDisplayName(users[0])}.`;
  }

  return `${users.length} users found. Choose one to see their record.`;
}

// The support console: find one user, see their profile and courses, and
// remove a student from a course.
//
// It is rendered only inside SupportConsoleGate, and every call it makes is
// checked again by the backend. What is searched for and who is open are this
// component's state: they are never put in the address bar or in browser
// storage, and they are gone when the page is left.
export function SupportConsolePage() {
  const headingId = useId();
  // The search asked for, and how many have been: the same text twice is two
  // searches.
  const [search, setSearch] = useState({ query: '', run: 0 });
  const [chosenUserId, setChosenUserId] = useState<string | null>(null);
  // What a removal did. It replaces the search's outcome until the next search.
  const [outcome, setOutcome] = useState('');
  const result = useSupportUserSearch(search.query, search.run);

  function handleSearch(query: string) {
    setSearch((previous) => ({ query, run: previous.run + 1 }));
    setChosenUserId(null);
    setOutcome('');
  }

  const searched = search.query !== '';
  const searching = searched && result.isPending;
  const users = searched && result.isSuccess ? result.data : null;
  // One match is opened without a click.
  const openUserId = chosenUserId ?? (users?.length === 1 ? users[0].id : null);
  const status = searching ? 'Searching…' : outcome || (users ? searchOutcome(users) : '');

  return (
    <section aria-labelledby={headingId} className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title} id={headingId}>
          Support console
        </h1>
        <p className={styles.lede}>
          Find a user by their Kudu user ID or their email address. Changes made here are
          recorded with your name.
        </p>
      </header>

      <Card className={styles.searchCard}>
        <SupportSearchForm busy={searching} onSearch={handleSearch} />

        {/* One place says what happened: a search's outcome, then a removal's. */}
        <p aria-live="polite" className={styles.status} role="status">
          {status}
        </p>

        {searched && result.isError ? (
          <p className={styles.failure} role="alert">
            {supportErrorMessage(result.error)}
          </p>
        ) : null}
      </Card>

      {users ? (
        <SupportSearchResults
          onOpen={(user) => setChosenUserId(user.id)}
          selectedUserId={openUserId}
          users={users}
        />
      ) : null}

      {openUserId ? (
        <SupportUserPanel key={openUserId} onAnnounce={setOutcome} userId={openUserId} />
      ) : null}
    </section>
  );
}
